import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { InspectionsApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { handleAdminSuccess, handleAdminError } from '../../common/SharedFunctions';
import { IInspection, IInspectionsData } from '../interfaces/interfaces';
import { useInspectionsModalStore } from '../store/useInspectionsModalStore';

const inspectionsApi = new InspectionsApi.InspectionsApi(applicationInsights);

export enum InspectionsQueryKeys {
    InspectionsState = 'inspectionsState',
}

export const useInspectionsStateQuery = () => {
    const { drawBudgetId } = useInspectionsModalStore();

    return useQuery<IInspectionsData>({
        queryKey: [InspectionsQueryKeys.InspectionsState, drawBudgetId],
        queryFn: async () => {
            const response = await inspectionsApi.getInspectionsState(drawBudgetId);
            return response.result as unknown as IInspectionsData;
        },
        enabled: !!drawBudgetId && drawBudgetId > 0,
        staleTime: 0,
    });
};

export const useCreateInspectionMutation = (drawBudgetId: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (model: { drawBudgetId: number; drawId: number | null; fileIds: number[] }) => {
            const response = await inspectionsApi.createInspection(model);
            if (response.statusCode !== 200) throw new Error('Create inspection failed');
            return response.result as IInspection;
        },
        onSuccess: async (inspection) => {
            const store = useInspectionsModalStore.getState();
            store.setIsNewInspection(false);

            await queryClient.refetchQueries({ queryKey: [InspectionsQueryKeys.InspectionsState, drawBudgetId] });
            const newData = queryClient.getQueryData<IInspectionsData>([InspectionsQueryKeys.InspectionsState, drawBudgetId]);
            const insp = newData?.inspections.find((i) => i.id === inspection.id);
            if (insp) store.setCurrentInspection(insp);

            handleAdminSuccess('Your inspection Order request was submitted successfully');
        },
        onError: () => {
            handleAdminError(new Error('Your inspection Order request failed.'), 'Your inspection Order request failed.');
        },
    });
};

export const useRefreshInspectionMutation = () =>
    useMutation({
        mutationFn: async (inspectionId: number) => {
            const response = await inspectionsApi.refreshInspection(inspectionId);
            if (response.statusCode !== 200) throw new Error('Refresh inspection failed');
            return response;
        },
        onSuccess: () => {
            handleAdminSuccess(
                "A request to refresh this inspection's Status has been requested. Please check back in a few minutes."
            );
        },
    });

export const useResetErrorMutation = (drawBudgetId: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (inspectionId: number) => {
            const response = await inspectionsApi.resetError(inspectionId);
            if (response.statusCode !== 200) throw new Error('Reset error failed');
            return response.result as unknown as IInspectionsData;
        },
        onSuccess: (newData, inspectionId) => {
            queryClient.setQueryData([InspectionsQueryKeys.InspectionsState, drawBudgetId], newData);

            const store = useInspectionsModalStore.getState();
            store.setDrawId(null);
            const insp = newData.inspections.find((i) => i.id === inspectionId);
            store.setCurrentInspection(insp ?? null);

            handleAdminSuccess(
                "A request to Reset this inspection's Error has been requested. Please check back later for further updates."
            );
        },
    });
};

export const useLinkToDrawMutation = (drawBudgetId: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ inspectionId, drawId }: { inspectionId: number; drawId: number }) => {
            const response = await inspectionsApi.linkToDraw(inspectionId, drawId);
            if (response.statusCode !== 200) throw new Error('Link to draw failed');
            return response.result as unknown as IInspectionsData;
        },
        onSuccess: (newData, { inspectionId }) => {
            queryClient.setQueryData([InspectionsQueryKeys.InspectionsState, drawBudgetId], newData);

            const store = useInspectionsModalStore.getState();
            store.setDrawId(null);
            const insp = newData.inspections.find((i) => i.id === inspectionId);
            store.setCurrentInspection(insp ?? null);

            handleAdminSuccess('The Inspection has been linked to the selected Draw successfully');
        },
    });
};
